import React, { useState, useEffect } from "react";
import {
  Stack,
  Checkbox,
  TextInput,
  Text,
  Select,
  MultiSelect,
  SimpleGrid,
  Paper,
  Tabs,
  Box,
} from "@mantine/core";
import RemoteSelect2 from "../remoteSelect2";
import McaNotificationTemplateInput from "./McaNotificationTemplateInput";
import EntityDocumentEmailFields from "../entityDocumentEmailFields";
import {
  defaultStepNotification,
  formatStaffLabel,
  getMcaNotificationTemplateVars,
  MCA_NOTIFY_BY,
  MCA_NOTIFY_CHANNELS,
} from "../../data/mcaEntities";
import { useMcaEditorReadOnly } from "./mcaEditorReadOnly";

const FieldCard = ({ title, children }) => (
  <Paper withBorder p="md" radius="sm">
    <Text size="xs" weight={600} color="dimmed" mb="sm" transform="uppercase">
      {title}
    </Text>
    {children}
  </Paper>
);

export const notificationSectionForError = (error) => {
  const message = String(error || "");
  if (message.startsWith("Email")) return "email";
  if (message.toLowerCase().includes("recipient")) return "recipients";
  return "setup";
};

const CLIENT_LINK_ENTITIES = new Set([
  "client",
  "order",
  "debitNote",
  "quotation",
  "orderInvestmentTransaction",
]);

const McaStepNotificationForm = ({
  value,
  onChange,
  targetEntity,
  section = "setup",
  onSectionChange,
}) => {
  const readOnly = useMcaEditorReadOnly();
  const [uncontrolledSection, setUncontrolledSection] = useState(section);
  const sectionValue = onSectionChange ? section : uncontrolledSection;
  const setSection = (next) => {
    if (onSectionChange) onSectionChange(next);
    else setUncontrolledSection(next);
  };
  const notification = { ...defaultStepNotification(), ...value };
  const templateVariables = getMcaNotificationTemplateVars(targetEntity);
  const [emailOptions, setEmailOptions] = useState(() => notification.notifyEmails ?? []);
  const showEntityUser = targetEntity === "user";
  const showEntityClient = CLIENT_LINK_ENTITIES.has(targetEntity);
  const showEntityConsultants = CLIENT_LINK_ENTITIES.has(targetEntity);
  const entityClientChecked =
    !!notification.notifyEntityClient ||
    (targetEntity === "client" && !!notification.notifyEntityUser);

  useEffect(() => {
    setEmailOptions((prev) => {
      const merged = [...prev];
      (notification.notifyEmails ?? []).forEach((e) => {
        if (e && !merged.includes(e)) merged.push(e);
      });
      return merged;
    });
  }, [notification.notifyEmails]);

  const patch = (p) => {
    if (readOnly) return;
    onChange({ ...notification, ...p });
  };

  const handleNotifyByChange = (notifyBy) => {
    patch({
      notifyBy,
      notifyEmails: notifyBy === "EMAILS" ? notification.notifyEmails : [],
      notifyUsers: notifyBy === "USERS" ? notification.notifyUsers : [],
      notifyRoles: notifyBy === "USER_ROLE" ? notification.notifyRoles : [],
    });
  };

  const isEmail = (notification.channel ?? "EMAIL") === "EMAIL";
  const activeSection = !isEmail && sectionValue === "email" ? "setup" : sectionValue;

  useEffect(() => {
    if (!isEmail && sectionValue === "email") setSection("setup");
    // setSection identity follows the latest onSectionChange
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEmail, sectionValue]);

  return (
    <Tabs
      value={activeSection}
      onTabChange={setSection}
      variant="pills"
      sx={(theme) => ({
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        "& [role='tab'][data-active]": {
          backgroundColor: `${theme.colors.cyan[0]} !important`,
          color: `${theme.colors.cyan[9]} !important`,
          fontWeight: 600,
        },
      })}
    >
      <Tabs.List>
        <Tabs.Tab value="setup">Setup</Tabs.Tab>
        <Tabs.Tab value="recipients">Recipients</Tabs.Tab>
        {isEmail && <Tabs.Tab value="email">Email</Tabs.Tab>}
      </Tabs.List>
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", paddingTop: 16 }}>
        <Tabs.Panel value="setup">
          <Stack spacing="md">
            <FieldCard title="Basic">
              <SimpleGrid cols={2} spacing="md" breakpoints={[{ maxWidth: "sm", cols: 1 }]}>
                <TextInput
                  label="Name"
                  required
                  size="sm"
                  placeholder="Notify maker on complete"
                  value={notification.name ?? ""}
                  readOnly={readOnly}
                  onChange={(e) => patch({ name: e.target.value })}
                />
                <Select
                  label="Channel"
                  size="sm"
                  data={MCA_NOTIFY_CHANNELS}
                  value={notification.channel ?? "EMAIL"}
                  readOnly={readOnly}
                  onChange={(v) => patch({ channel: v ?? "EMAIL" })}
                />
              </SimpleGrid>
            </FieldCard>
            <FieldCard title="When to send">
              <SimpleGrid cols={2} spacing="sm" breakpoints={[{ maxWidth: "sm", cols: 1 }]}>
                <Checkbox
                  label="Send on start of the step"
                  checked={!!notification.notifyOnStart}
                  disabled={readOnly}
                  onChange={(e) => patch({ notifyOnStart: e.currentTarget.checked })}
                />
                <Checkbox
                  label="Send on completion of the step"
                  checked={notification.notifyOnComplete !== false}
                  disabled={readOnly}
                  onChange={(e) => patch({ notifyOnComplete: e.currentTarget.checked })}
                />
              </SimpleGrid>
            </FieldCard>
            {!isEmail && (
              <Text size="sm" color="dimmed">
                SMS delivery is not configured yet. This rule will be saved but not sent.
              </Text>
            )}
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="recipients">
          <Stack spacing="md">
            <FieldCard title="Notify">
              <Stack spacing="sm">
                <Select
                  label="Notify to"
                  size="sm"
                  data={MCA_NOTIFY_BY}
                  value={notification.notifyBy ?? "USERS"}
                  readOnly={readOnly}
                  onChange={handleNotifyByChange}
                />
                {notification.notifyBy === "EMAILS" && (
                  <MultiSelect
                    label="Email addresses"
                    size="sm"
                    placeholder="Type email and press Enter"
                    data={emailOptions}
                    searchable
                    creatable
                    value={notification.notifyEmails ?? []}
                    readOnly={readOnly}
                    getCreateLabel={(query) => `+ Add ${query}`}
                    onCreate={(query) => {
                      const trimmed = query.trim();
                      if (!trimmed) return null;
                      setEmailOptions((current) =>
                        current.includes(trimmed) ? current : [...current, trimmed]
                      );
                      return trimmed;
                    }}
                    onChange={(notifyEmails) => patch({ notifyEmails })}
                  />
                )}
                {notification.notifyBy === "USERS" && (
                  <RemoteSelect2
                    form={{
                      values: notification,
                      setFieldValue: (field, val) => patch({ [field]: val }),
                      markUserEdited: () => {},
                    }}
                    name="notifyUsers"
                    label="Notify users"
                    apiEntity="user"
                    labelRender={formatStaffLabel}
                    multiple
                    size="sm"
                    readOnly={readOnly}
                    placeholder="Search users"
                    searchFields={["name", "staffId", "cname", "email"]}
                  />
                )}
                {notification.notifyBy === "USER_ROLE" && (
                  <RemoteSelect2
                    form={{
                      values: notification,
                      setFieldValue: (field, val) => patch({ [field]: val }),
                      markUserEdited: () => {},
                    }}
                    name="notifyRoles"
                    label="Notify roles"
                    apiEntity="userRole"
                    labelField="name"
                    multiple
                    size="sm"
                    readOnly={readOnly}
                    placeholder="Search roles"
                    searchFields={["name"]}
                  />
                )}
              </Stack>
            </FieldCard>
            <FieldCard title="Also include">
              <SimpleGrid cols={2} spacing="sm" breakpoints={[{ maxWidth: "sm", cols: 1 }]}>
                <Checkbox
                  label="Maker"
                  checked={!!notification.notifyMaker}
                  disabled={readOnly}
                  onChange={(e) => patch({ notifyMaker: e.currentTarget.checked })}
                />
                <Checkbox
                  label="Next step approvers"
                  checked={!!notification.notifyNextApprovers}
                  disabled={readOnly}
                  onChange={(e) =>
                    patch({ notifyNextApprovers: e.currentTarget.checked })
                  }
                />
                {showEntityUser && (
                  <Checkbox
                    label="Entity user"
                    description="Sends to the User login email (email field), not personal email"
                    checked={!!notification.notifyEntityUser}
                    disabled={readOnly}
                    onChange={(e) =>
                      patch({ notifyEntityUser: e.currentTarget.checked })
                    }
                  />
                )}
                {showEntityClient && (
                  <Checkbox
                    label="Entity client"
                    description="Sends to the client's first Contact Info email (default if marked), then related-person contact"
                    checked={entityClientChecked}
                    disabled={readOnly}
                    onChange={(e) => {
                      const checked = e.currentTarget.checked;
                      patch({
                        notifyEntityClient: checked,
                        ...(targetEntity === "client"
                          ? { notifyEntityUser: false }
                          : {}),
                      });
                    }}
                  />
                )}
                {showEntityConsultants && (
                  <Checkbox
                    label="Entity consultants"
                    description="Sends to all linked consultants' login emails"
                    checked={!!notification.notifyEntityConsultants}
                    disabled={readOnly}
                    onChange={(e) =>
                      patch({ notifyEntityConsultants: e.currentTarget.checked })
                    }
                  />
                )}
              </SimpleGrid>
            </FieldCard>
          </Stack>
        </Tabs.Panel>

        {isEmail && (
          <Tabs.Panel value="email">
            <EntityDocumentEmailFields
              grouped
              between={
                <FieldCard title="Message">
                  <Stack spacing="sm">
                    <McaNotificationTemplateInput
                      label="Email subject"
                      required
                      placeholder="Step completed: {{requestCode}}"
                      value={notification.subject ?? ""}
                      readOnly={readOnly}
                      onChange={(subject) => patch({ subject })}
                      variables={templateVariables}
                    />
                    <McaNotificationTemplateInput
                      label="Email message"
                      required
                      multiline
                      minRows={8}
                      placeholder="Request {{requestCode}} — step {{stepName}}"
                      value={notification.message ?? ""}
                      readOnly={readOnly}
                      onChange={(message) => patch({ message })}
                      variables={templateVariables}
                    />
                  </Stack>
                </FieldCard>
              }
              targetEntity={targetEntity}
              templateType="MCA"
              passwordTemplateType="MCA_PASSWORD"
              subject={notification.subject ?? ""}
              body={notification.message ?? ""}
              cc={notification.cc ?? ""}
              bcc={notification.bcc ?? ""}
              attachmentKeys={notification.attachmentKeys ?? []}
              encrypt={!!notification.encrypt}
              passwordSubject={notification.passwordSubject ?? ""}
              passwordBody={notification.passwordBody ?? ""}
              readOnly={readOnly}
              onChange={(partial) => {
                const next = { ...partial };
                if (Object.prototype.hasOwnProperty.call(partial, "body")) {
                  next.message = partial.body;
                  delete next.body;
                }
                patch(next);
              }}
            />
          </Tabs.Panel>
        )}
      </Box>
    </Tabs>
  );
};

export default McaStepNotificationForm;
